import {Component, Injector, OnInit} from '@angular/core';
import {BaseComponent} from '../../base.component';
import {RouteUtil} from '../../../util/route.util';
import {FactorStatusHelper} from '../../../enums/factor.status';
import {RequestType} from '../../../enums/request.type';
import {DataResponse} from '../../../network/service/data.response';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {ListResponse} from '../../../network/service/list.response';

@Component({
  selector: 'app-home',
  standalone: false,
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent extends BaseComponent implements OnInit {
  dashboardStats = new FormGroup(this.buildDashboardControls());
  branches: any[] = [];
  selectedBranchId?: string;
  selectedPeriod = 'DAILY';
  revenueByPeriodChartData: any[] = [];
  isLoading = true;
  isLoadingStats = true;
  isLoadingCharts = true;
  isNavigating = false;
  isNavigatingToClients = false;
  private readonly defaultDashboardValues = {
    dailyReport: {
      count: 0,
      income: 0
    },
    weeklyReport: {
      count: 0,
      income: 0
    },
    monthlyReport: {
      count: 0,
      income: 0
    },
    yearlyReport: {
      count: 0,
      income: 0
    },
    totalReport: {
      count: 0,
      income: 0
    },
    totalUsers: 0,
    popularItems: [] as any[],
    recentOrders: [] as any[],
    daysBeforeNowReport: [] as any[],
    hourlyReport: [] as any[],
    factorStatusCounts: {
      submit: 0,
      pendingPayment: 0,
      progressing: 0,
      preparation: 0,
      processingCenter: 0,
      deliveryDispatcher: 0,
      delivered: 0,
      canceled: 0,
      canceledBySystem: 0
    }
  };

  constructor(injector: Injector) {
    super(injector);
  }

  get daily() {
    return this.dashboardStats.get('dailyReport') as FormGroup;
  }

  get weekly() {
    return this.dashboardStats.get('weeklyReport') as FormGroup;
  }

  get monthly() {
    return this.dashboardStats.get('monthlyReport') as FormGroup;
  }

  get yearly() {
    return this.dashboardStats.get('yearlyReport') as FormGroup;
  }

  get total() {
    return this.dashboardStats.get('totalReport') as FormGroup;
  }

  get daysBeforeNowReportFA() {
    return this.dashboardStats.get('daysBeforeNowReport') as FormArray;
  }

  get popularItems() {
    return this.dashboardStats.get('popularItems') as FormArray;
  }

  get hourlyReport() {
    return this.dashboardStats.get('hourlyReport') as FormArray;
  }

  get recentOrders() {
    return this.dashboardStats.get('recentOrders') as FormArray;
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  override loadOnline() {
    super.loadOnline();
    this.serviceApi.getBranches(undefined, undefined, undefined, RequestType.ALL, undefined).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        this.branches = res.data;
        if (this.branches && this.branches.length > 0) {
          this.onBranchClick(this.branches[0]);
        }
      }
    });
    this.serviceApi.getClients(undefined, undefined, undefined, RequestType.PAGINATE, {
      Filter: {}
    }).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.dashboardStats.get('totalUsers')?.setValue(res?.count ?? 0);
      }
    });
  }

  onBranchClick(branch: any) {
    if (this.selectedBranchId !== branch.BusinessId) {
      this.selectedBranchId = branch.BusinessId;
      this.loadDashboardData();
    }
  }

  isBranchSelected(branch: any): boolean {
    return this.selectedBranchId === branch.BusinessId;
  }

  loadDashboardData() {
    this.isLoading = true;
    this.dashboardStats.reset(this.defaultDashboardValues);
    this.daysBeforeNowReportFA.clear();
    this.popularItems.clear();
    this.recentOrders.clear();
    this.hourlyReport.clear();
    this.loadStatistics();
    this.getIncomesSummeryPerHour();
  }

  loadStatistics() {
    this.isLoadingStats = true;
    this.isLoadingCharts = true;
    this.serviceApi.getIncomesSummery(this.selectedBranchId).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        this.dashboardStats.get('dailyReport')?.patchValue({
          count: res.data.dailyReport.Count,
          income: res.data.dailyReport.Income,
        });
        this.dashboardStats.get('weeklyReport')?.patchValue({
          count: res.data.weeklyReport.Count,
          income: res.data.weeklyReport.Income,
        });
        this.dashboardStats.get('monthlyReport')?.patchValue({
          count: res.data.monthlyReport.Count,
          income: res.data.monthlyReport.Income,
        });
        this.dashboardStats.get('yearlyReport')?.patchValue({
          count: res.data.yearlyReport.Count,
          income: res.data.yearlyReport.Income,
        });
        this.dashboardStats.get('totalReport')?.patchValue({
          count: res.data.totalReport.Count,
          income: res.data.totalReport.Income,
        });
        this.revenueByPeriodChartData = [
          {
            period: 'امروز',
            revenue: res.data.dailyReport.Income
          },
          {
            period: 'هفته',
            revenue: res.data.weeklyReport.Income
          },
          {
            period: 'ماه',
            revenue: res.data.monthlyReport.Income
          },
          {
            period: 'سال',
            revenue: res.data.yearlyReport.Income
          }
        ];
      }
      this.isLoadingStats = false;
      this.isLoadingCharts = false;
      this.isLoading = false;
    });
    this.serviceApi.getIncomesBeforeNow(7, this.selectedBranchId).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        for (const report of res.data) {
          (this.dashboardStats.get('daysBeforeNowReport') as FormArray).push(
            new FormGroup({
              date: new FormControl(report.Date),
              count: new FormControl(report.Count),
              income: new FormControl(report.Income),
            })
          );
        }
      }
      this.isLoadingStats = false;
      this.isLoadingCharts = false;
      this.isLoading = false;
    });
    this.serviceApi.getServiceIncomesSummery(this.selectedBranchId).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        for (const report of res.data) {
          (this.dashboardStats.get('popularItems') as FormArray).push(
            new FormGroup({
              count: new FormControl(report.Count),
              income: new FormControl(report.Income),
              serviceTitle: new FormControl(report.ServiceTitle),
              categoryTitle: new FormControl(report.CategoryTitle)
            })
          );
        }
      }
      this.isLoadingStats = false;
      this.isLoadingCharts = false;
      this.isLoading = false;
    });
    this.serviceApi.getFactors(0, 5, 'User,FactorOrders', undefined, {
      Filter: {},
      Order: {
        Field: 'ModifyDate',
        Type: 'DESC'
      }
    }, this.selectedBranchId).subscribe((res: ListResponse<any>) => {
      if (res.success && res.data) {
        for (const report of res.data) {
          (this.dashboardStats.get('recentOrders') as FormArray).push(
            new FormGroup({
              factorId: new FormControl(report.FactorId),
              userId: new FormControl(report.UserId),
              customer: new FormControl(report.User.FirstName + ' ' + report.User.LastName + '(' + report.User.MobileNumber + ')'),
              amount: new FormControl(report.PayablePrice),
              status: new FormControl(this.getStatusText(report.Status)),
              statusColor: new FormControl(this.getStatusColor(report.Status)),
              statusIcon: new FormControl(this.getStatusIcon(report.Status)),
              date: new FormControl(report.ModifyDate)
            })
          );
        }
      }
    });
    this.serviceApi.getFactorStatusCounts(this.selectedBranchId).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        this.dashboardStats.get('factorStatusCounts')?.setValue({
          submit: res.data.find((s: any) => s.StatusCode === "SUBMIT")?.Count,
          pendingPayment: res.data.find((s: any) => s.StatusCode === "PENDING_PAYMENT").Count,
          progressing: res.data.find((s: any) => s.StatusCode === "PROGRESSING").Count,
          preparation: res.data.find((s: any) => s.StatusCode === "PREPARATION").Count,
          processingCenter: res.data.find((s: any) => s.StatusCode === "PROCESSING_CENTER").Count,
          deliveryDispatcher: res.data.find((s: any) => s.StatusCode === "DELIVERY_DISPATCHER").Count,
          delivered: res.data.find((s: any) => s.StatusCode === "DELIVERED").Count,
          canceled: res.data.find((s: any) => s.StatusCode === "CANCELED").Count,
          canceledBySystem: res.data.find((s: any) => s.StatusCode === "CANCELED_BY_SYSTEM").Count,
        });
      }
      this.isLoadingStats = false;
      this.isLoadingCharts = false;
      this.isLoading = false;
    });
  }

  getIncomesSummeryPerHour() {
    this.hourlyReport.clear();
    this.serviceApi.getIncomesSummeryPerHour(this.selectedPeriod, this.selectedBranchId).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        for (const report of res.data) {
          (this.dashboardStats.get('hourlyReport') as FormArray).push(
            new FormGroup({
              hour: new FormControl(report.Hour),
              count: new FormControl(report.Count)
            })
          );
        }
      }
      this.isLoadingStats = false;
    });
  }

  onPeriodChange(period: string) {
    this.selectedPeriod = period;
    this.getIncomesSummeryPerHour();
  }

  formatCurrency(amount: number | string | null | undefined): string {
    if (amount === null || amount === undefined || amount === '') return '—';
    const n = typeof amount === 'string' ? Number(amount) : amount;
    if (Number.isNaN(n)) return '—';
    return new Intl.NumberFormat('fa-IR', {maximumFractionDigits: 0}).format(n) + ' ریال';
  }

  getStatusColor(status: string): string {
    return FactorStatusHelper.getStatusColor(status);
  }

  getStatusText(status: string): string {
    const statusKey = FactorStatusHelper.getStatusText(status);
    return this.translateService.instant(statusKey);
  }

  getStatusIcon(status: string): string {
    return FactorStatusHelper.getStatusIcon(status);
  }

  getMaxIncomes(): number {
    const revenues = this.daysBeforeNowReportFA.controls.map(
      ctrl => Number(ctrl.get('income')?.value ?? 0)
    );
    return revenues.length ? Math.max(...revenues) : 0;
  }

  getMaxOrders(): number {
    const revenues = this.hourlyReport.controls.map(
      ctrl => Number(ctrl.get('count')?.value ?? 0)
    );
    return revenues.length ? Math.max(...revenues) : 0;
  }

  getMaxPopularItems(): number {
    const popular = this.popularItems.controls.map(
      ctrl => Number(ctrl.get('count')?.value ?? 0)
    );
    return popular.length ? Math.max(...popular) : 0;
  }

  getMaxRevenueByPeriod(): number {
    if (this.revenueByPeriodChartData.map(d => d.revenue).some(d => d > 0)) {
      return Math.max(...this.revenueByPeriodChartData.map(d => d.revenue));
    } else {
      return 1;
    }
  }

  viewAllFactors() {
    this.isNavigating = true;
    this.router.navigate([RouteUtil.DASHBOARD_MODULE, RouteUtil.ORDERS_ADMIN_COMPONENT]).then(() => {
      this.isNavigating = false;
    }).catch(() => {
      this.isNavigating = false;
    });
  }

  createNewOrder() {
    this.router.navigate([RouteUtil.DASHBOARD_MODULE, RouteUtil.PLACE_ORDER_COMPONENT]).then();
  }

  manageClients() {
    this.isNavigatingToClients = true;
    this.router.navigate([RouteUtil.DASHBOARD_MODULE, RouteUtil.CLIENTS_COMPONENT]).then(() => {
      this.isNavigatingToClients = false;
    }).catch(() => {
      this.isNavigatingToClients = false;
    });
  }

  openSettings() {
  }

  openReports() {
  }

  viewUserDetail(userId: string) {
    this.router.navigate([RouteUtil.DASHBOARD_MODULE, RouteUtil.USER_DETAIL_COMPONENT, {UserId: userId}]).then();
  }

  private buildDashboardControls() {
    return {
      dailyReport: new FormGroup({
        count: new FormControl<number>(0),
        income: new FormControl<number>(0)
      }),
      weeklyReport: new FormGroup({
        count: new FormControl<number>(0),
        income: new FormControl<number>(0)
      }),
      monthlyReport: new FormGroup({
        count: new FormControl<number>(0),
        income: new FormControl<number>(0)
      }),
      yearlyReport: new FormGroup({
        count: new FormControl<number>(0),
        income: new FormControl<number>(0)
      }),
      totalReport: new FormGroup({
        count: new FormControl<number>(0),
        income: new FormControl<number>(0)
      }),
      totalUsers: new FormControl<number>(0),
      popularItems: new FormArray<FormGroup>([]),
      recentOrders: new FormArray<FormGroup>([]),
      daysBeforeNowReport: new FormArray<FormGroup>([]),
      hourlyReport: new FormArray<FormGroup>([]),
      factorStatusCounts: new FormGroup({
        submit: new FormControl<number>(0),
        pendingPayment: new FormControl<number>(0),
        progressing: new FormControl<number>(0),
        preparation: new FormControl<number>(0),
        processingCenter: new FormControl<number>(0),
        deliveryDispatcher: new FormControl<number>(0),
        delivered: new FormControl<number>(0),
        canceled: new FormControl<number>(0),
        canceledBySystem: new FormControl<number>(0),
      })
    };
  }
}
